import { useQuery } from '@tanstack/react-query';
import { Loader2, Wallet } from 'lucide-react';
import { api } from '@/api/client';

interface Balance {
  merchantId: number;
  balance?: number | string;
  available?: number | string;
  billingMode?: string;
  postpaidLimit?: number | string;
}

/**
 * Money fields arrive as STRINGS, not numbers — Prisma serialises `Decimal` to a string, so the API
 * sends `"amount":"626.7"`. The previous version called `t.amount.toFixed(2)` directly and threw
 * "toFixed is not a function", which blanked the whole tab.
 *
 * It went unnoticed because almost no merchant had a wallet transaction, so the table never rendered
 * — the tab always short-circuited to "No transactions yet". The 2026-08-10 wallet backfill gave
 * every merchant tens of thousands of rows and the latent crash surfaced immediately.
 */
interface Txn {
  id: number;
  txnType: string;
  amount: number | string;
  balanceAfter?: number | string;
  refType?: string | null;
  refId?: string | null;
  remarks?: string | null;
  createdAt: string;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const inr = (v: unknown) => `₹${num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TYPE_STYLE: Record<string, string> = {
  CREDIT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DEBIT: 'bg-rose-50 text-rose-700 border-rose-200',
  REFUND: 'bg-sky-50 text-sky-700 border-sky-200',
  ADJUSTMENT: 'bg-amber-50 text-amber-700 border-amber-200',
  REMITTANCE_PAID: 'bg-violet-50 text-violet-700 border-violet-200',
};

const PAGE = 25;

export function MerchantWalletTab({ merchantId }: { merchantId: number }) {
  const balQ = useQuery({
    queryKey: ['merchant-wallet-balance', merchantId],
    queryFn: async () => (await api.get<Balance>(`/merchants/${merchantId}/wallet/balance`)).data,
  });
  const txnQ = useQuery({
    queryKey: ['merchant-wallet-txn', merchantId],
    queryFn: async () => (await api.get<Txn[]>(`/merchants/${merchantId}/wallet/transactions`, { params: { limit: PAGE } })).data,
  });

  if (balQ.isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  const balance = num(balQ.data?.balance);
  const available = num(balQ.data?.available);
  const isPostpaid = balQ.data?.billingMode === 'POSTPAID';
  const rows = txnQ.data ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card label="Wallet balance" value={inr(balance)} negative={balance < 0} icon />
        <Card
          label={isPostpaid ? 'Available (incl. credit limit)' : 'Available to ship'}
          value={inr(available)}
          negative={available < 0}
        />
        <Card
          label="Billing mode"
          value={balQ.data?.billingMode ?? '—'}
          sub={isPostpaid ? `Credit limit ${inr(balQ.data?.postpaidLimit)}` : 'Cannot go below zero'}
          isText
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Recent transactions</div>
          {rows.length >= PAGE && (
            <a href="/wallet-ledger" className="text-xs text-primary hover:underline">
              View all in Wallet Ledger →
            </a>
          )}
        </div>
        {txnQ.isLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : txnQ.isError ? (
          <p className="text-sm text-rose-600">Could not load transactions.</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Date</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Reference</th>
                  <th className="text-left px-4 py-2">Details</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-right px-4 py-2">Balance after</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const amt = num(t.amount);
                  const isDebit = t.txnType === 'DEBIT';
                  return (
                    <tr key={t.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 whitespace-nowrap text-slate-600">
                        {new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`border rounded-full px-2 py-0.5 text-[11px] ${TYPE_STYLE[t.txnType] ?? 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {t.txnType === 'REMITTANCE_PAID' ? 'Remittance' : t.txnType.charAt(0) + t.txnType.slice(1).toLowerCase()}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500 capitalize whitespace-nowrap">
                        {t.refType ?? '—'}{t.refId ? ` · ${t.refId}` : ''}
                      </td>
                      <td className="px-4 py-2 text-slate-600 max-w-[280px] truncate" title={t.remarks ?? ''}>{t.remarks ?? '—'}</td>
                      <td className={`px-4 py-2 text-right font-mono tabular-nums ${isDebit ? 'text-rose-700' : 'text-emerald-700'}`}>
                        {isDebit ? '−' : '+'}{inr(amt)}
                      </td>
                      <td className={`px-4 py-2 text-right font-mono tabular-nums ${num(t.balanceAfter) < 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                        {t.balanceAfter == null ? '—' : inr(t.balanceAfter)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, sub, icon, isText, negative }: {
  label: string; value: string; sub?: string; icon?: boolean; isText?: boolean; negative?: boolean;
}) {
  return (
    <div className="p-4 border border-slate-200 rounded-lg">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
        {icon && <Wallet size={14} />} {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${negative ? 'text-rose-700' : 'text-slate-900'} ${isText ? 'text-lg' : ''}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
